/**
 * LeaderboardPage — ranks published content by engagement score across
 * Instagram / Facebook / TikTok / LinkedIn for the selected company + period.
 *
 * Engagement score (must mirror backend /api/leaderboard):
 *   likes + comments*3 + shares*5 + saves*4 + clicks*2
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, Loader2, Heart, MessageSquare, Share, Eye, TrendingUp, AlertTriangle, Instagram, Facebook, Linkedin, AtSign } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import PageHelp from "@/components/PageHelp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_META = {
  instagram: { icon: Instagram, color: "#E4405F", label: "Instagram" },
  facebook: { icon: Facebook, color: "#1877F2", label: "Facebook" },
  tiktok: { icon: AtSign, color: "#000000", label: "TikTok" },
  linkedin: { icon: Linkedin, color: "#0A66C2", label: "LinkedIn" },
};

const RANK_BADGE = (rank) => {
  if (rank === 1) return { cls: "bg-amber-400 text-black", emoji: "🥇" };
  if (rank === 2) return { cls: "bg-gray-300 text-black", emoji: "🥈" };
  if (rank === 3) return { cls: "bg-amber-700 text-white", emoji: "🥉" };
  return { cls: "bg-[var(--studio-bg)] text-[var(--studio-text-muted)] border border-[var(--studio-border)]", emoji: `#${rank}` };
};

export default function LeaderboardPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [days, setDays] = useState("30");
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => setCompanies(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { days };
    if (companyId !== "all") params.company_id = companyId;
    if (platform !== "all") params.platform = platform;
    Promise.all([
      axios.get(`${API}/leaderboard`, { params: { ...params, limit: 20 }, withCredentials: true }),
      axios.get(`${API}/leaderboard/totals`, { params: { days, ...(companyId !== "all" ? { company_id: companyId } : {}) }, withCredentials: true }),
    ])
      .then(([lb, tot]) => {
        setItems(lb.data?.items || []);
        setTotals(tot.data);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Laden fehlgeschlagen"))
      .finally(() => setLoading(false));
  }, [companyId, platform, days]);

  const hasData = items.length > 0;

  return (
    <div data-testid="leaderboard-page" className="space-y-6 max-w-7xl">
      <PageHelp title="So liest du das Performance-Leaderboard" storageKey="leaderboard">
        <p>
          Hier siehst du, welche Posts am besten performt haben — sortiert nach <b>Engagement-Score</b>.
        </p>
        <h4>Wie wird der Score berechnet?</h4>
        <p>
          <code>Score = Likes + 3 × Kommentare + 5 × Shares + 4 × Saves + 2 × Klicks</code>
        </p>
        <p>
          So zaehlen <i>aktive Interaktionen</i> wie Kommentare und Shares deutlich mehr als reine Likes — das spiegelt echte Wirkung wider.
          <b>Reach</b> wird angezeigt, aber nicht in den Score eingerechnet, damit kleinere Accounts fair mitkommen.
        </p>
        <h4>Filter nutzen</h4>
        <ul>
          <li><b>Firma</b> — nur eine Firma anzeigen, oder alle</li>
          <li><b>Plattform</b> — IG / FB / TikTok / LinkedIn</li>
          <li><b>Tage</b> — Zeitraum (7 / 30 / 90 / 365)</li>
        </ul>
        <h4>Was kannst du damit anfangen?</h4>
        <ul>
          <li>Erkenne <b>Top-Performer</b> und recycle den Stil/Hook in neuen Posts.</li>
          <li>Vergleiche Plattform-Performance: welcher Kanal liefert?</li>
          <li>Lade einen <b>PDF-Report</b> ueber die Performance-Seite herunter, um deinen Kunden den Erfolg zu zeigen.</li>
        </ul>
      </PageHelp>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight flex items-center gap-3">
            <Trophy className="w-8 h-8 text-amber-500" /> Performance-Leaderboard
          </h1>
          <p className="text-[var(--studio-text-muted)] mt-1">
            Welche Posts performten am besten? Engagement-Score = Likes + 3·Kommentare + 5·Shares + 4·Saves + 2·Klicks
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="w-48 h-9 text-sm" data-testid="leaderboard-company-filter">
              <SelectValue placeholder="Firma" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Firmen</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger className="w-36 h-9 text-sm" data-testid="leaderboard-platform-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Plattformen</SelectItem>
              {Object.entries(PLATFORM_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-32 h-9 text-sm" data-testid="leaderboard-days-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 Tage</SelectItem>
              <SelectItem value="30">30 Tage</SelectItem>
              <SelectItem value="90">90 Tage</SelectItem>
              <SelectItem value="365">365 Tage</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Totals Cards */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="leaderboard-totals">
          <StatCard icon={TrendingUp} label="Posts" value={totals.totals?.posts || 0} color="#2563EB" />
          <StatCard icon={Heart} label="Likes" value={totals.totals?.likes || 0} color="#EF4444" />
          <StatCard icon={MessageSquare} label="Kommentare" value={totals.totals?.comments || 0} color="#3B82F6" />
          <StatCard icon={Share} label="Shares" value={totals.totals?.shares || 0} color="#10B981" />
          <StatCard icon={Eye} label="Reach" value={totals.totals?.reach || 0} color="#F59E0B" />
        </div>
      )}

      {/* Bar chart: score by day */}
      {totals?.by_day?.length > 0 && (
        <Card className="border-[var(--studio-border)] shadow-none" data-testid="score-trend-chart">
          <CardHeader>
            <CardTitle className="font-heading text-base">Engagement-Verlauf</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={totals.by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="score" fill="#2563EB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard list */}
      <Card className="border-[var(--studio-border)] shadow-none">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center justify-between">
            <span>Top {items.length || ""} Posts</span>
            {totals?.engagement_rate !== undefined && (
              <Badge variant="secondary" className="text-xs" data-testid="overall-engagement-rate">
                Ø Engagement-Rate: {totals.engagement_rate} %
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2" data-testid="leaderboard-list">
          {loading ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#2563EB]" /></div>
          ) : !hasData ? (
            <div className="py-12 text-center" data-testid="leaderboard-empty">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
              <p className="text-sm font-medium">Keine veroeffentlichten Posts im Zeitraum</p>
              <p className="text-xs text-[var(--studio-text-muted)] mt-1">
                Sobald Posts publiziert + Performance-Metriken aktualisiert sind, erscheint die Rangliste hier.
              </p>
            </div>
          ) : items.map((it, idx) => {
            const rank = idx + 1;
            const meta = PLATFORM_META[it.platform] || { icon: AtSign, color: "#666", label: it.platform };
            const Icon = meta.icon;
            const badge = RANK_BADGE(rank);
            const m = it.metrics || {};
            return (
              <div
                key={it.id}
                className="flex items-start gap-3 p-3 rounded-lg border border-[var(--studio-border)] hover:bg-[var(--studio-bg)] transition"
                data-testid={`leaderboard-row-${rank}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${badge.cls}`}>
                  {badge.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Icon className="w-4 h-4" style={{ color: meta.color }} />
                    <span className="text-xs font-semibold">{meta.label}</span>
                    {it.format_type && (
                      <Badge variant="secondary" className="text-[10px]">{it.format_type}</Badge>
                    )}
                    {companyId === "all" && it.company_name && (
                      <span className="text-[10px] text-[var(--studio-text-muted)]">· {it.company_name}</span>
                    )}
                    <span className="text-[10px] text-[var(--studio-text-muted)] ml-auto">
                      {it.published_at ? new Date(it.published_at).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }) : ""}
                    </span>
                  </div>
                  <p className="text-sm mt-1 line-clamp-2">
                    {it.caption || <i className="text-[var(--studio-text-muted)]">(ohne Caption)</i>}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-[var(--studio-text-muted)] flex-wrap">
                    <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {m.likes || 0}</span>
                    <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {m.comments || 0}</span>
                    <span className="flex items-center gap-1"><Share className="w-3 h-3" /> {m.shares || 0}</span>
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {m.reach || 0}</span>
                    <span className="ml-auto font-mono font-bold text-[#2563EB]" data-testid={`leaderboard-score-${rank}`}>
                      Score: {it.score}
                    </span>
                    <span className="font-mono">ER: {it.engagement_rate}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="border-[var(--studio-border)] shadow-none">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-[var(--studio-text-muted)]">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          {label}
        </div>
        <p className="text-2xl font-bold font-heading mt-1" style={{ color }}>
          {value.toLocaleString("de-CH")}
        </p>
      </CardContent>
    </Card>
  );
}
